// src/pages/Dashboard.js
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  FormControl,
  Grid,
  Input,
  Option,
  Select,
  TextField,
  Typography,
  useColorScheme,
} from "@mui/joy";
import { useMediaQuery, useTheme } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { AiOutlineWarning } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import countriesData from "../data/countries.json";
import Overview from "./Overview";
const countries = countriesData.countries;
const nonSovereign = new Set(countriesData.nonSovereign);

export default function Dashboard({
  categories1 = [], // categories from App
  transactions = [], // transactions from App
  budgets = [], // budgets from App
  onAddTransaction, // add handler from App

}) {
  const { user, completeProfileFunc } = useContext(AuthContext);
  const [firstName, setFirstName] = useState(null);
  const [lastName, setLastName] = useState(null);
  const [country, setCountry] = useState(null);
  const [countryPhoneCode, setCountryPhoneCode] = useState(null);
  const [countryLabel, setCountryLabel] = useState(country?.label);
  const [birthDate, setBirthDate] = useState(null);
  const [profilePicture, setProfilePicture] = useState(null);
  const [cityList, setCityList] = useState(null);
  const [city, setCity] = useState(null);
  const theme = useTheme();
  const { mode, setMode } = useColorScheme();
  const [phoneNumber, setPhoneNumber] = useState("");
  const navigate = useNavigate();
  const isMedium = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isLarge = useMediaQuery(theme.breakpoints.down("lg"));

  const [error, setError] = useState("");

  // NOTE:
  // transactions, categories1 and budgets are provided by App.jsx (no local fetch here).
  // If you still need to mutate them locally, call the lifted setters passed from App.
  // Example: use liftTransactions([...]) to replace app transactions, or call onAddTransaction(newTx).

  const handleFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setProfilePicture(imageUrl);
    }
  };

  const incompleteProfile = !(
    firstName &&
    lastName &&
    country &&
    city &&
    phoneNumber &&
    birthDate
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const [month, day, year] = birthDate?.split("/") || [];
    const sqlFormattedBirthDate = `${year}-${month?.padStart(
      2,
      "0"
    )}-${day?.padStart(2, "0")}`;

    if (incompleteProfile) {
      setError("Please fill in all the inputs to continue.");
      return;
    }

    const ok = await completeProfileFunc(
      firstName,
      lastName,
      profilePicture || profilePicture === null,
      countryLabel,
      city,
      phoneNumber,
      sqlFormattedBirthDate
    );
    if (ok) {
      navigate("/dashboard");
    } else {
      setError("Profile update failed. Please try again.");
    }
  };

  const sovereignCountries = countries.filter((c) => !nonSovereign.has(c.code));
  const completeProfile =
    user.first_name &&
    user.last_name &&
    user.country &&
    user.city &&
    user.phone_number &&
    user.date_of_birth;
  const [apiError, setApiError] = useState(null);

  useEffect(() => {
    if (!country?.code) return;

    const headers = new Headers({
      "X-CSCAPI-KEY": process.env.REACT_APP_CSCAPI_KEY,
    });

    fetch(
      `https://api.countrystatecity.in/v1/countries/${country.code}/states`,
      {
        method: "GET",
        headers,
        redirect: "follow",
      }
    )
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setCityList(data);
      })
      .catch((err) => {
        console.error(err);
        setApiError(err.message);
      });
  }, [country?.code]);

  const formatBirth = (raw) => {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  };

  const handleBirthChange = (e) => {
    const next = formatBirth(e.target.value);
    setBirthDate(next);
  };

  // debug to verify App is passing data in correctly
  useEffect(() => {
    // console.log("Dashboard received categories1:", categories1);
    // console.log("Dashboard received transactions:", transactions?.length ?? 0);
    // console.log("Dashboard received budgets:", budgets?.length ?? 0);
  }, [categories1, transactions, budgets]);

  return (
    <>
      {!completeProfile ? (
        <Grid
          sx={{
            display: "flex",
            justifySelf: isMedium || isSmall ? "flex-end" : "center",
            px: 2,
            py: 2,
            bgcolor:
              mode === "light" ? "#ebebeb" : "hsla(240, 7.70%, 2.50%, 0.15)",
            border:
              mode === "light" ? null : "0.5px solid rgba(229, 231, 235, 0.14)",
            flexDirection: "column",
            gap: 5,
            width: "100%",
            maxWidth:  isSmall ? "100%" :isMedium ? "90%" : "50%",
            marginTop: "1%",
            height:{xs:"110%",sm: "100%"},
          }}
        >
          {error ? (
            <Alert color="danger" variant="plain">
              <AiOutlineWarning size={"3%"} />
              {error}
            </Alert>
          ) : null}

          <Typography
            sx={{
              fontWeight: 500,
              lineHeight: 2,
              fontSize: "2.5rem",
              fontFamily: "monospace",
            }}
          >
            Before we move on...
          </Typography>
          <Typography sx={{ fontFamily: "monospace" }}>
            Please help us complete your profile to use FinTrack.
          </Typography>

          <FormControl sx={{gap:1.5}}>
            {/* form controls same as before (omitted here for brevity in this snippet) */}
            <Box>
              <Typography sx={{ fontFamily: "monospace" }}>
                First Name
              </Typography>
              <Input
                autoComplete="new-password"
                type="name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                error={error && !firstName}
              />
            </Box>
            <Box>
              <Typography sx={{ fontFamily: "monospace" }}>
                Last Name
              </Typography>
              <Input
                autoComplete="new-password"
                type="name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                error={error && !lastName}
              />
            </Box>

            <Box>
              <Typography sx={{ fontFamily: "monospace" }}>
                Birth Date
              </Typography>
              <Input
                placeholder="MM/DD/YYYY"
                value={birthDate}
                onChange={handleBirthChange}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "\\d{2}/\\d{2}/\\d{4}",
                  maxLength: 10,
                }}
                sx={{
                  borderRadius: "7px",
                  width: isMedium || isSmall ? "100%" : "50%",
                }}
                error={error && !birthDate}
              />
            </Box>

            <Typography sx={{ fontFamily: "monospace" }}>
              {"Profile Picture (Optional)"}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 2,
                alignItems: "center",
                width: profilePicture !== null ? "60%" : "60%",
              }}
            >
              <input
                type="file"
                accept="image/*"
                style={{ width: "100%", cursor: "pointer" }}
                onChange={handleFileChange}
              />
              {profilePicture !== null ? (
                <Avatar src={profilePicture} sx={{ fontSize: 50 }} />
              ) : null}
            </Box>

            <Box>
              <Typography sx={{ fontFamily: "monospace" }}>Country</Typography>
              <Autocomplete
                id="country-select-demo"
                openOnFocus={false}
                sx={{ width: "50%" }}
                options={sovereignCountries}
                autoHighlight
                getOptionLabel={(option) => option.label}
                value={country}
                error={error && !country}
                onChange={(_, newValue) => {
                  if (newValue === null) {
                    setCityList(null);
                    setCountry(null);
                    setCountryPhoneCode(null);
                    setCountryLabel("");
                  } else {
                    setCountry(newValue);
                    setCountryPhoneCode(parseInt(newValue.phone ?? "", 10));
                    setCountryLabel(newValue.label ?? "");
                  }
                }}
                renderOption={(props, option) => {
                  const { key, ...optProps } = props;
                  return (
                    <Grid
                      key={key}
                      component="li"
                      sx={{
                        "& > img": { mr: 2, flexShrink: 0, ml: 1 },
                        color: "text.primary",
                      }}
                      {...optProps}
                    >
                      <img
                        loading="lazy"
                        width="20"
                        srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                        src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                        alt=""
                      />
                      {option.label} ({option.code}) +{option.phone}
                    </Grid>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Choose a country"
                    slotProps={{ htmlInput: { autoComplete: "new-password" } }}
                  />
                )}
              />
            </Box>

            <Box>
              <Typography sx={{ fontFamily: "monospace" }}>State</Typography>
              <Select
                placeholder="Select a state"
                disabled={!country}
                onChange={(_, newValue) => setCity(newValue)}
                error={error && !city}
              >
                {cityList && cityList.length > 0 ? (
                  cityList.map((ct) => (
                    <Option key={ct.id} value={ct.name}>
                      {ct.name}
                    </Option>
                  ))
                ) : (
                  <Option disabled key="no-cities">
                    No states available
                  </Option>
                )}
              </Select>
            </Box>

            <Box>
              <Typography sx={{ fontFamily: "monospace" }}>
                Phone Number
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                <Input
                  type="number"
                  autoComplete="new-password"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  error={error && !phoneNumber}
                  required
                  startAdornment={
                    countryPhoneCode && (
                      <Grid
                        position="start"
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <img
                          src={`https://flagcdn.com/w20/${country?.code.toLowerCase()}.png`}
                          alt={countryLabel}
                          style={{ width: 20, height: 15 }}
                        />
                        <span style={{ color: "#cbd5e1" }}>
                          +{countryPhoneCode}
                        </span>
                      </Grid>
                    )
                  }
                  startDecorator={
                    country ? (
                      <Grid
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <img
                          loading="lazy"
                          width="100%"
                          style={{ borderRadius: "20%" }}
                          src={`https://flagcdn.com/w20/${country.code.toLowerCase()}.png`}
                          srcSet={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png 2x`}
                          alt={`${country.label} flag`}
                        />
                        <Typography>
                          {countryPhoneCode != null
                            ? `+${countryPhoneCode}`
                            : ""}
                        </Typography>
                      </Grid>
                    ) : (
                      <Box
                        component="span"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 20,
                          height: 15,
                          bgcolor: "grey.300",
                          color: mode === "light" ? "black" : "grey.600",
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                          borderRadius: 0.5,
                        }}
                      >
                        ?
                      </Box>
                    )
                  }
                />
              </Box>
            </Box>

            <Button
              onClick={handleSubmit}
              type="submit"
              variant="outlined"
              sx={{
                color: "inherit",
                border:
                  mode === "light"
                    ? "0.5px solid hsla(240, 7.70%, 2.50%, 0.15)"
                    : "0.5px solid rgba(229, 231, 235, 0.14)",
                width: "60%",
                boxShadow: "3.1px 6.2px 6.2px hsl(0deg 0% 0% / 0.40)",
                "&:hover": { transform: "translateY(-5%)" },
                alignSelf: "center",
                cursor: "pointer",
                transition: "all 0.3s ease",
                marginTop: 5,
              }}
            >
              <Typography className="login" fontFamily={"monospace"}>
                Complete your profile.
              </Typography>
            </Button>
          </FormControl>
        </Grid>
      ) : (
        <Grid
          sx={{
            display: "flex",
            justifySelf: isMedium || isSmall || isLarge ? "flex-end" : "center",
            px: 2,
            py: 2,
            flexDirection: "column",
            gap: 5,
            width: "100%",
            maxWidth: isMedium || isSmall ? "100%" : isLarge? "93%": "70%",
            borderRadius: "2px",
            height: isSmall ? "110%":"100%",
            
          }}
        >
          <Overview
            transactions={transactions}
            categories={categories1}
            budgets={budgets}
            onAddTransaction={onAddTransaction}
          />
        </Grid>
      )}
    </>
  );
}
